import React, { useState, useEffect } from 'react';
import { X, Zap, Crown, CheckCircle, CreditCard, Wallet, MessageCircle, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// API URL helper
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const WHATSAPP_LINK = "https://wa.me/message/3PBT5TZW3SARB1";
const QR_CODE_URL = "https://customer-assets.emergentagent.com/job_b4c8a9d0-7ea2-48c0-817a-eed35237bb3f/artifacts/0i16gwr9_qrccc.jpeg";
const WALLET_ADDRESS = "0x8c492198161A497276475B57880E1F2FD20e192D";
const DEFAULT_SQUARE_LINK = "https://square.link/u/pcgnBKgL";

// Pro upgrade pricing
const PRO_PRICING = {
  fullPrice: 99.99,
  proratedMonthly: 40.00, // Prorated amount for existing monthly subscribers
};

const PRO_FEATURES = [
  'Advanced Binary Scalper Algorithm',
  'Real-time Signal Strength Indicator',
  'Multi-timeframe Analysis (1m to 4h)',
  'Automatic High Payout Asset Filter',
  'Technical Indicators Panel (EMA, RSI, BB)',
  'Signal History with Performance Tracking',
  'Low Payout Protection System',
  'Priority Scanner with Auto-resume',
  'Clean Professional Interface',
];

const UpgradeModal = ({ isOpen, onClose, user, onUpgradeSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [squareLink, setSquareLink] = useState(DEFAULT_SQUARE_LINK);

  // Fetch the Pro upgrade payment link from backend
  useEffect(() => {
    if (isOpen) {
      fetch(`${API}/upgrade/pro-payment-link`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.payment_link) {
            setSquareLink(data.payment_link);
          }
        })
        .catch(err => {
          console.error('Error fetching pro payment link:', err);
        });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Check if user has monthly plan for prorated pricing
  const hasMonthlyPlan = user?.current_plan?.toLowerCase().includes('monthly') || 
                         user?.current_plan?.toLowerCase().includes('premium');
  const upgradePrice = hasMonthlyPlan ? PRO_PRICING.proratedMonthly : PRO_PRICING.fullPrice;

  const copyWalletAddress = () => {
    navigator.clipboard.writeText(WALLET_ADDRESS);
    setCopiedAddress(true);
    toast.success('Wallet address copied!');
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  const handlePayWithCard = () => {
    setPaymentMethod('card');
    setAwaitingPayment(true);
    toast.info('Redirecting to Square payment...');
    setTimeout(() => {
      window.open(squareLink, '_blank');
    }, 500);
  };

  const handlePaymentComplete = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/upgrade/request-pro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user?.email,
          payment_method: paymentMethod,
          amount: upgradePrice,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Upgrade request submitted! Awaiting admin approval.');
        onUpgradeSuccess?.();
        onClose();
      } else {
        toast.error(data.message || 'Failed to submit upgrade request');
      }
    } catch (error) {
      console.error('Upgrade request error:', error);
      toast.error('Failed to process upgrade. Please contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-700 bg-gradient-to-r from-purple-900/50 to-pink-900/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Upgrade to Pro</h2>
                <p className="text-sm text-gray-400">Unlock the Binary Scalper Bot</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Pricing */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Pro Upgrade</p>
                <p className="text-white font-semibold">Binary Scalper Bot</p>
                {hasMonthlyPlan && (
                  <p className="text-xs text-green-400 mt-1">
                    ✓ Monthly subscriber discount applied!
                  </p>
                )}
              </div>
              <div className="text-right">
                {hasMonthlyPlan && (
                  <p className="text-sm text-gray-500 line-through">${PRO_PRICING.fullPrice}</p>
                )}
                <p className="text-2xl font-bold text-purple-400">${upgradePrice.toFixed(2)}</p>
                <p className="text-xs text-gray-400">one-time upgrade</p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-400" />
              Pro Features Included
            </h3>
            <div className="grid gap-2">
              {PRO_FEATURES.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {/* Payment Section */}
          {!awaitingPayment ? (
            <>
              <h3 className="text-lg font-semibold text-white mb-4">Choose Payment Method</h3>
              
              <div className="space-y-3">
                {/* Pay with Card */}
                <button
                  onClick={handlePayWithCard}
                  className="w-full p-4 rounded-xl border-2 border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-bold">Pay with Card</p>
                        <p className="text-blue-400 text-xs">Secure payment via Square</p>
                      </div>
                    </div>
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                      RECOMMENDED
                    </span>
                  </div>
                </button>

                {/* Pay with Crypto */}
                <button
                  onClick={() => setPaymentMethod('crypto')}
                  className="w-full p-4 rounded-xl border-2 border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-xl flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-bold">Pay with Crypto</p>
                        <p className="text-orange-400 text-xs">USDT on BNB Smart Chain</p>
                      </div>
                    </div>
                    <p className="text-orange-300 text-xs">Up to 6h review</p>
                  </div>
                </button>
              </div>
            </>
          ) : paymentMethod === 'card' ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <h4 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Complete Your Square Payment
                </h4>
                <p className="text-gray-300 text-sm mb-3">
                  A new tab should have opened for Square payment. After completing your payment, click the button below.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={handlePaymentComplete}
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3"
                  >
                    {submitting ? 'Processing...' : "I've Completed Payment"}
                  </Button>
                  <Button
                    onClick={handlePayWithCard}
                    variant="outline"
                    className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    Open Square Payment Again
                  </Button>
                  <button 
                    onClick={() => {
                      setPaymentMethod(null);
                      setAwaitingPayment(false);
                    }}
                    className="w-full text-gray-400 hover:text-white text-sm py-2"
                  >
                    Use different payment method
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Crypto Payment Details */}
          {paymentMethod === 'crypto' && !awaitingPayment && (
            <div className="mt-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <div className="mb-3">
                <button 
                  onClick={() => setPaymentMethod(null)}
                  className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                >
                  ← Change payment method
                </button>
              </div>

              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-orange-400" />
                Pay with USDT (BEP-20)
              </h4>
              
              {/* QR Code */}
              <div className="flex justify-center mb-4">
                <div className="p-2 bg-white rounded-lg">
                  <img 
                    src={QR_CODE_URL} 
                    alt="Payment QR Code" 
                    className="w-32 h-32 rounded"
                  />
                </div>
              </div>
              
              {/* Wallet Address */}
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-1">Wallet Address (BNB Smart Chain)</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-slate-900 rounded text-xs text-gray-300 break-all font-mono">
                    {WALLET_ADDRESS}
                  </code>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={copyWalletAddress}
                    className="shrink-0 border-slate-600"
                  >
                    {copiedAddress ? <CheckCircle className="w-4 h-4 text-green-400" /> : 'Copy'}
                  </Button>
                </div>
              </div>
              
              {/* Amount */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                <p className="text-amber-300 text-sm text-center font-medium">
                  Send exactly: <strong>{upgradePrice.toFixed(2)} USDT</strong>
                  <br />
                  <span className="text-xs">Network: BNB Smart Chain (BEP-20)</span>
                </p>
              </div>
              
              {/* WhatsApp Confirmation */}
              <a 
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white text-center rounded-lg font-bold transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Send Payment Screenshot via WhatsApp
              </a>
            </div>
          )}

          {/* Note */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-300 font-medium">How it works</p>
                <p className="text-gray-400 text-sm mt-1">
                  After payment, your account will be upgraded to Pro within <strong className="text-white">30 minutes</strong>. 
                  You can then switch between Standard and Pro views anytime.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
